import React, { useState } from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { Button } from 'src/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import { Calendar } from 'src/components/ui/calendar';
import { Label } from 'src/components/ui/label';
import { Checkbox } from 'src/components/ui/checkbox';

export const MonthYearRangePicker = ({
  onChange,
  value,
  className = 'border-blue-100',
  hidePresent = false,
  hasError = false,
}) => {
  const [startPopoverOpen, setStartPopoverOpen] = useState(false);
  const [endPopoverOpen, setEndPopoverOpen] = useState(false);

  const currentYear = new Date().getFullYear();
  const employmentPeriod = value || {};
  const { startMonth, startYear, endMonth, endYear, present } =
    employmentPeriod;

  // Convert month/year to Date object (first day of month)
  const toDate = (month, year) => {
    if (!month || !year) return undefined;
    return new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
  };

  // Convert Date to month/year
  const fromDate = (date) => {
    if (!date) return { month: '', year: '' };
    return {
      month: String(date.getMonth() + 1).padStart(2, '0'),
      year: String(date.getFullYear()),
    };
  };

  const startDate = toDate(startMonth, startYear);
  const endDate = toDate(endMonth, endYear);

  const handleStartDateChange = (date) => {
    if (!date) {
      onChange({
        ...employmentPeriod,
        startMonth: '',
        startYear: '',
      });
      setStartPopoverOpen(false);
      return;
    }

    // Ensure we only use the first day of the selected month
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const { month, year } = fromDate(firstDayOfMonth);
    const newPeriod = {
      ...employmentPeriod,
      startMonth: month,
      startYear: year,
    };

    // Clear end date if it becomes invalid
    if (endYear && parseInt(endYear, 10) < parseInt(year, 10)) {
      newPeriod.endMonth = '';
      newPeriod.endYear = '';
    } else if (
      endYear === year &&
      endMonth &&
      parseInt(endMonth, 10) < parseInt(month, 10)
    ) {
      newPeriod.endMonth = '';
      newPeriod.endYear = '';
    }

    onChange(newPeriod);
    setStartPopoverOpen(false);
  };

  const handleEndDateChange = (date) => {
    if (!date) {
      onChange({
        ...employmentPeriod,
        endMonth: '',
        endYear: '',
      });
      setEndPopoverOpen(false);
      return;
    }

    // Ensure we only use the first day of the selected month
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const { month, year } = fromDate(firstDayOfMonth);
    onChange({
      ...employmentPeriod,
      endMonth: month,
      endYear: year,
    });
    setEndPopoverOpen(false);
  };

  // Reuse the Education form's error styling so inputs + date buttons match exactly.
  const errorClass = hasError ? 'edu-fi edu-fi--error' : '';

  // Get minimum end date (start date or current date, whichever is earlier)
  const getMinEndDate = () => {
    if (!startDate) return undefined;
    const today = new Date();
    return startDate > today ? today : startDate;
  };

  // Get maximum end date (current date)
  const getMaxEndDate = () => new Date();

  return (
    <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
      {/* From Date */}
      <div>
        <Label className="text-xs font-semibold text-gray-500 mb-1.5 block">
          From
        </Label>
        <Popover open={startPopoverOpen} onOpenChange={setStartPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                'w-full justify-start text-left font-normal h-10 bg-[var(--surface)]',
                !startDate && 'text-muted-foreground',
                className,
                errorClass
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4 opacity-50 flex-shrink-0" />
              <span className="truncate">
                {startDate
                  ? format(startDate, 'MMM yyyy')
                  : 'Select month & year'}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" sideOffset={4}>
            <Calendar
              mode="single"
              selected={startDate}
              onSelect={handleStartDateChange}
              captionLayout="dropdown-buttons"
              fromYear={1980}
              toYear={currentYear}
              defaultMonth={startDate || new Date(currentYear, 0, 1)}
              disabled={(date) => {
                const today = new Date();
                const currentMonth = new Date(
                  today.getFullYear(),
                  today.getMonth(),
                  1
                );
                // Disable future dates - compare by month/year only
                const dateMonth = new Date(
                  date.getFullYear(),
                  date.getMonth(),
                  1
                );
                return dateMonth > currentMonth;
              }}
              classNames={{
                caption_label: 'hidden',
                dropdown_month: 'flex justify-center text-sm font-medium',
                dropdown_year: 'flex justify-center text-sm font-medium',
                dropdown_icon: 'hidden',
              }}
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* To Date */}
      <div>
        <Label className="text-xs font-semibold text-gray-500 mb-1.5 block">
          To
        </Label>
        <div className="flex flex-wrap items-center gap-2">
          <Popover open={endPopoverOpen} onOpenChange={setEndPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                disabled={!!present}
                className={cn(
                  'w-full justify-start text-left font-normal h-10',
                  !endDate && 'text-muted-foreground',
                  className,
                  errorClass
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4 opacity-50 flex-shrink-0" />
                <span className="truncate">
                  {endDate
                    ? format(endDate, 'MMM yyyy')
                    : 'Select month & year'}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start" sideOffset={4}>
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={handleEndDateChange}
                captionLayout="dropdown-buttons"
                fromYear={startYear ? parseInt(startYear, 10) : 1980}
                toYear={currentYear}
                defaultMonth={
                  endDate || startDate || new Date(currentYear, 0, 1)
                }
                disabled={(date) => {
                  const minDate = getMinEndDate();
                  const today = new Date();
                  const currentMonth = new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    1
                  );
                  const dateMonth = new Date(
                    date.getFullYear(),
                    date.getMonth(),
                    1
                  );
                  // Disable dates before start date
                  if (minDate) {
                    const minMonth = new Date(
                      minDate.getFullYear(),
                      minDate.getMonth(),
                      1
                    );
                    if (dateMonth < minMonth) return true;
                  }
                  // Disable future dates
                  if (dateMonth > currentMonth) return true;
                  return false;
                }}
                classNames={{
                  caption_label: 'hidden',
                  dropdown_month: 'flex justify-center text-sm font-medium',
                  dropdown_year: 'flex justify-center text-sm font-medium',
                  dropdown_icon: 'hidden',
                }}
              />
            </PopoverContent>
          </Popover>

          {/* Present checkbox */}
          {!hidePresent && (
            <div className="ml-0 flex items-center gap-2 md:ml-2">
              <Checkbox
                checked={!!present}
                onCheckedChange={(checked) =>
                  onChange({
                    ...employmentPeriod,
                    present: !!checked,
                    ...(checked ? { endMonth: '', endYear: '' } : {}),
                  })
                }
                id="employment-present"
                className="h-4 w-4"
              />
              <Label
                htmlFor="employment-present"
                className="text-xs font-semibold text-gray-700 cursor-pointer"
              >
                Present
              </Label>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
